import * as React from 'react';
import { Minus, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Composer, type ComposerProps } from './composer';

export type ChatMode = 'panel' | 'full';

export interface ChatHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  avatar?: React.ReactNode;
  online?: boolean;
  onClose?: () => void;
  mode?: ChatMode;
}

/** Title bar for the chat surface. */
export function ChatHeader({
  title = 'Langbridge 도우미',
  subtitle = '온라인',
  avatar = 'L',
  online = true,
  onClose,
  mode = 'panel',
}: ChatHeaderProps) {
  return (
    <div className="flex h-[var(--header-height)] shrink-0 items-center gap-[var(--space-2-5)] bg-header-bg px-4 text-header-fg">
      <div className="flex size-[34px] shrink-0 items-center justify-center rounded-full bg-accent text-md font-bold text-accent-fg">
        {avatar}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-md font-bold leading-tight">{title}</div>
        <div className="flex items-center gap-[var(--space-1-5)] text-xs opacity-80">
          {online ? <span className="size-1.5 rounded-full bg-[var(--color-online)]" /> : null}
          {subtitle}
        </div>
      </div>
      {onClose ? (
        <button
          type="button"
          aria-label={mode === 'full' ? '닫기' : '최소화'}
          onClick={onClose}
          className="flex cursor-pointer border-none bg-transparent p-1 text-current opacity-75 hover:opacity-100"
        >
          {mode === 'full' ? <X className="size-[18px]" /> : <Minus className="size-[18px]" />}
        </button>
      ) : null}
    </div>
  );
}

export interface ChatWindowProps extends ChatHeaderProps {
  mode?: ChatMode;
  children?: React.ReactNode;
  composerProps?: ComposerProps;
  showComposer?: boolean;
  poweredBy?: boolean;
  className?: string;
}

/**
 * Shared chat shell (S2 web app + S3 widget). `panel` is a docked card with
 * panel radius/shadow; `full` fills its container with no chrome. One component,
 * adaptive layout.
 */
export function ChatWindow({
  mode = 'panel',
  title,
  subtitle,
  avatar,
  online = true,
  onClose,
  children,
  composerProps,
  showComposer = true,
  poweredBy = true,
  className,
}: ChatWindowProps) {
  const isPanel = mode === 'panel';
  return (
    <div
      className={cn(
        'flex max-w-full flex-col overflow-hidden bg-surface',
        isPanel
          ? 'h-[var(--panel-max-height)] w-[var(--panel-width)] rounded-[var(--panel-radius)] border border-border shadow-panel'
          : 'size-full',
        className,
      )}
    >
      <ChatHeader
        title={title}
        subtitle={subtitle}
        avatar={avatar}
        online={online}
        onClose={onClose}
        mode={mode}
      />
      <div className="flex flex-1 flex-col gap-[var(--bubble-gap)] overflow-y-auto bg-[var(--color-chat-canvas)] p-4">
        {children}
      </div>
      {showComposer ? <Composer {...composerProps} /> : null}
      {poweredBy ? (
        <div className="bg-surface p-[var(--space-1-5)] text-center text-2xs text-fg-muted">
          ⚡ Powered by <b className="text-fg-secondary">Langbridge</b>
        </div>
      ) : null}
    </div>
  );
}
